import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconType } from '@se/cp.common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

interface IMultiIconCellRendererConfig {
    icon: string;
    tooltip?: string;
    iconSize?: string;
    iconType?: IconType;
    field: string;
    hidden: boolean;
    property: string;
    additionalInfo: string;
    showAdditionalInfo: boolean;
}

interface IMultiIconCellRendererView extends IMultiIconCellRendererConfig {
    title: string;
}

type IMultiIconCellRendererParams = ICellRendererParams & {
    estensa: IMultiIconCellRendererConfig[];
    onClick?: (event: MouseEvent, data: any) => void;
};

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-multi-icon-cell-renderer',
    templateUrl: './multi-icon-cell-renderer.component.html',
    styleUrls: ['./multi-icon-cell-renderer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIconModule, TranslateModule],
})
export class MultiIconCellRendererComponent implements ICellRendererAngularComp {
    private readonly translate = inject(TranslateService);

    readonly icons = signal<IMultiIconCellRendererView[]>([]);
    readonly hasButton = signal<boolean>(false);

    onClick: (event: MouseEvent) => void = (_event: MouseEvent) => null;

    agInit(params: IMultiIconCellRendererParams): void {
        this.refresh(params);
    }

    refresh(params: IMultiIconCellRendererParams): boolean {
        const icons: IMultiIconCellRendererView[] = [];

        const configs = params.estensa ?? [];
        for (const cfg of configs) {
            const visibleFlag = this.getByPath(params.value, cfg.field);
            const hidden = !visibleFlag;
            const entity = (params as any).data?.entity;

            const value =
                entity && typeof entity.getPropertyValue === 'function' ? entity.getPropertyValue(cfg.property) : null;

            const titleParts: string[] = [];
            if (cfg.tooltip) titleParts.push(this.translate.instant(cfg.tooltip));
            if (cfg.showAdditionalInfo && cfg.additionalInfo) titleParts.push(cfg.additionalInfo);
            if (value != null && value !== '') titleParts.push(String(value));

            icons.push({
                ...cfg,
                hidden,
                iconSize: cfg.iconSize ?? '16px',
                title: titleParts.join(' '),
            });
        }
        if (params.onClick) {
            this.onClick = (ev: MouseEvent) => params.onClick!(ev, params.data);
        } else {
            this.onClick = () => null;
        }
        this.hasButton.set(!!params.onClick);
        this.icons.set(icons);
        return true;
    }

    private getByPath(obj: unknown, path: string): unknown {
        if (obj == null) return undefined;
        const p = (path ?? '').trim();
        if (!p) return undefined;

        return p.split('.').reduce<any>((o, k) => (o != null ? o[k] : undefined), obj as any);
    }
}
